/**
 * Analytics Dashboard
 * Shows usage statistics and conversation metrics
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

function Analytics() {
    const { getAccessTokenSilently } = useAuth0();
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadAnalytics();
    }, []);

    const loadAnalytics = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();

            const response = await axios.get('/api/admin/analytics', {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setAnalytics(response.data.analytics);
            setError(null);
        } catch (err) {
            setError('Failed to load analytics. Make sure you are authenticated.');
            console.error('Analytics error:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="container-fluid py-4">
                <div className="text-center py-5">
                    <div className="spinner-lighthouse"></div>
                    <p className="mt-3 text-muted">Loading analytics...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid py-4">
                <div className="alert alert-warning">
                    <h5>Demo Mode</h5>
                    <p className="mb-0">{error}</p>
                    <p className="mt-2 mb-0 small">
                        In production, this dashboard would require Auth0 authentication.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            <h2 className="mb-4">Analytics Dashboard</h2>

            {/* Usage Stats */}
            <div className="row g-4 mb-4">
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <h6 className="text-muted mb-2">Total Messages</h6>
                            <h2 className="mb-0">{analytics?.messages?.total || 0}</h2>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <h6 className="text-muted mb-2">Active Conversations</h6>
                            <h2 className="mb-0">{analytics?.conversations?.active || 0}</h2>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <h6 className="text-muted mb-2">Total Brands</h6>
                            <h2 className="mb-0">{analytics?.brands?.total || 0}</h2>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm">
                        <div className="card-body">
                            <h6 className="text-muted mb-2">Usage</h6>
                            <h2 className="mb-0">{analytics?.usage?.percentage || 0}%</h2>
                            <small className="text-muted">
                                {analytics?.usage?.current || 0} / {analytics?.usage?.limit || 0}
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            {/* Channel Breakdown */}
            <div className="row g-4">
                <div className="col-md-6">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white">
                            <h5 className="mb-0">Conversations by Channel</h5>
                        </div>
                        <div className="card-body">
                            <div className="d-flex justify-content-between mb-3">
                                <span>📱 SMS</span>
                                <strong>{analytics?.conversations?.bySMS || 0}</strong>
                            </div>
                            <div className="d-flex justify-content-between">
                                <span>💬 Web Chat</span>
                                <strong>{analytics?.conversations?.byWeb || 0}</strong>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white">
                            <h5 className="mb-0">Brands</h5>
                        </div>
                        <div className="card-body">
                            {analytics?.brands?.list?.map((brand) => (
                                <div key={brand._id} className="d-flex justify-content-between mb-2">
                                    <span>{brand.name}</span>
                                    <span className="badge bg-secondary">{brand.industry}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Analytics;
